import { Select, Space } from 'antd'
import React from 'react'
import { useTranslation } from 'react-i18next'
import { parseDate, formatAsDate } from '../../../../../../../config/dayjsConfig'
import { FormattedInputNumber } from '../../../../../../../components/Misc/FormattedInputNumber'
import { TimePeriodOptions } from '../../../../../../../redux/context/customReports/types'
import { CustomReportCategory, OffsetUnit } from '../../../../../../../redux/context/customReports/typesCategory'
import { DatePicker } from '../../../../../../../components/Form'

const MonthPicker = (props: any) => <DatePicker {...props} picker="month" />
const { RangePicker } = DatePicker
const YearPicker = (props: any) => <DatePicker {...props} picker="year" />

// Define which time periods need an offset control
const OFFSET_ENABLED_PERIODS = [
  TimePeriodOptions.CurrentMonth,
  TimePeriodOptions.Past12months,
  TimePeriodOptions.Quarter,
  TimePeriodOptions.YearToDate,
  TimePeriodOptions.Previousyeartodate,
  TimePeriodOptions.FiscalyearToDate,
  TimePeriodOptions.PreviousFiscalyearToDate,
  TimePeriodOptions.Currentfiscalyear,
  TimePeriodOptions.PreviousFiscalyear,
  TimePeriodOptions.CurrentYear,
  TimePeriodOptions.FiscalyearFirstMonth,
  TimePeriodOptions.CumulativeFiscalyear,
  TimePeriodOptions.CurrentWeek,
  TimePeriodOptions.CurrentDay
]

// Define the config structure type to make TypeScript happy
type TimeControlConfig = {
  component: React.ComponentType<any>
  getProps: (category: CustomReportCategory, onChange: (c: Partial<CustomReportCategory>) => void) => any
}

// Configuration object that maps time period options to their UI controls
export const TIME_PERIOD_CONFIG: Record<string, TimeControlConfig> = {
  // Custom period using date range picker
  [TimePeriodOptions.CustomPeriod]: {
    component: RangePicker,
    getProps: (category: CustomReportCategory, onChange: (c: Partial<CustomReportCategory>) => void) => ({
      format: 'YYYY-MM',
      value: [
        category.startDate ? parseDate(category.startDate) : null,
        category.endDate ? parseDate(category.endDate) : null
      ],
      onPanelChange: (dates: any) => {
        dates &&
          onChange({
            startDate: formatAsDate(dates[0]?.startOf('month')),
            endDate: formatAsDate(dates[1]?.endOf('month'))
          })
      },
      mode: ['month', 'month']
    })
  },

  // Custom month using month picker
  [TimePeriodOptions.CustomMonth]: {
    component: MonthPicker,
    getProps: (category: CustomReportCategory, onChange: (c: Partial<CustomReportCategory>) => void) => ({
      value: category.startDate ? parseDate(category.startDate) : null,
      onChange: (date: string | null) => {
        const parsedDate = parseDate(date)
        onChange({
          startDate: formatAsDate(parsedDate?.startOf('month')),
          endDate: formatAsDate(parsedDate?.endOf('month'))
        })
      }
    })
  },

  // Custom year using year picker
  [TimePeriodOptions.CustomYear]: {
    component: YearPicker,
    getProps: (category: CustomReportCategory, onChange: (c: Partial<CustomReportCategory>) => void) => ({
      value: category.startDate ? parseDate(category.startDate) : null,
      onChange: (date: string | null) => {
        const parsedDate = parseDate(date)
        onChange({
          startDate: formatAsDate(parsedDate?.startOf('year')),
          endDate: formatAsDate(parsedDate?.endOf('year'))
        })
      }
    })
  },

  // From-to date using month picker
  [TimePeriodOptions.FromToDate]: {
    component: MonthPicker,
    getProps: (category: CustomReportCategory, onChange: (c: Partial<CustomReportCategory>) => void) => ({
      value: category.startDate ? parseDate(category.startDate) : null,
      onChange: (date: string | null) => {
        const parsedDate = parseDate(date)
        onChange({
          startDate: formatAsDate(parsedDate?.startOf('month'))
        })
      }
    })
  }
}

// Offset controls as a separate component for reusability
export const OffsetControls: React.FC<{
  category: CustomReportCategory
  onChange: (c: Partial<CustomReportCategory>) => void
}> = ({ category, onChange }) => {
  const { t } = useTranslation()

  // Only render if the current time period option supports offsets
  if (!OFFSET_ENABLED_PERIODS.includes(category.value as TimePeriodOptions)) {
    return null
  }

  return (
    <Space>
      <FormattedInputNumber
        placeholder="offset"
        value={category.offset}
        onChange={offset => {
          onChange({ offset: offset as number })
          if (!category.offsetUnit) {
            onChange({ offsetUnit: OffsetUnit.month })
          }
        }}
      />
      <Select
        value={category.offsetUnit || OffsetUnit.month}
        onChange={offsetUnit => onChange({ offsetUnit })}
        style={{ width: 100 }}
        placeholder="offset unit"
      >
        {Object.keys(OffsetUnit).map((val: string) => (
          <Select.Option value={val} key={val}>
            {t(`global:${val}`)}
          </Select.Option>
        ))}
      </Select>
    </Space>
  )
}

// Main component to render the appropriate time period control
export const TimePeriodControl: React.FC<{
  category: CustomReportCategory
  onChange: (c: Partial<CustomReportCategory>) => void
}> = ({ category, onChange }) => {
  // Safely check if there's a configuration for this time period
  const timePeriodValue = category.value?.toString() || ''
  const config = TIME_PERIOD_CONFIG[timePeriodValue]

  // If there's a specific component for this time period, render it
  if (config) {
    const Component = config.component
    return <Component {...config.getProps(category, onChange)} />
  }

  // Otherwise, return null (no additional control needed)
  return null
}
